import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Trash2, FileSpreadsheet } from "lucide-react";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Page, Panel } from "../components/Layout";
import CustomerSelect from "../components/CustomerSelect";
import { api, notifyErr, fmtDate, download } from "../lib/api";
import { useCustomers, useSelectedCustomer } from "../lib/hooks";
import { Tone } from "../lib/labels";

export default function Report() {
  const { customers } = useCustomers();
  const [cid, setCid] = useSelectedCustomer(customers);
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState("");
  const [predictives, setPredictives] = useState([]);
  const [predictiveId, setPredictiveId] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [dateMode, setDateMode] = useState("latest");
  const [reportDate, setReportDate] = useState("");
  const load = () => cid && api.get(`/reports?customer_id=${cid}`).then((r) => setReports(r.data));

  useEffect(() => {
    let active = true;
    setPredictives([]);
    setPredictiveId("");

    if (cid) {
      api.get(`/customers/${cid}`)
        .then((r) => {
          if (!active) return;
          const list = r.data.predictives_list || [];
          setPredictives(list);
          setPredictiveId(list.length ? list[0].id : "");
        })
        .catch((e) => {
          if (active) notifyErr(e);
        });
    }

    return () => { active = false; };
  }, [cid]);

  useEffect(() => {
    api.get("/categories")
      .then((r) => {
        setCategories(r.data);
        setSelectedCategories(r.data.map((x) => x.code));
      })
      .catch(notifyErr);
  }, []);
  useEffect(() => { load(); // eslint-disable-next-line
  }, [cid]);
  const gen = async (type, format) => {
    setBusy(type + format);
    try {
      const r = (await api.post("/reports", { customer_id: cid, type, format, predictive_id: predictiveId || null })).data;
      toast.success(`Report generato: ${r.rows} righe`);
      load();
      await download(`/reports/${r.id}/download`, r.filename);
    } catch (e) { notifyErr(e); } finally { setBusy(""); }
  };
  const genCategories = async () => {
    if (!cid || !selectedCategories.length ||
        (dateMode === "day" && !reportDate)) return;

    setBusy("categoriesxlsx");
    try {
      const r = (await api.post("/reports", {
        customer_id: cid,
        type: "internal",
        format: "xlsx",
        predictive_id: predictiveId || null,
        group_by_category: true,
        categories: selectedCategories,
        date_mode: dateMode,
        report_date: dateMode === "day" ? reportDate : null,
      })).data;

      toast.success(`Excel per categorie generato: ${r.rows} registrazioni`);
      load();
      await download(`/reports/${r.id}/download`, r.filename);
    } catch (e) {
      notifyErr(e);
    } finally {
      setBusy("");
    }
  };

  const del = async (id) => { if (window.confirm("Eliminare il file report?")) { await api.delete(`/reports/${id}`).catch(notifyErr); load(); } };
  const Card = ({ type, title, desc }) => (
    <Panel title={title}>
      <p className="text-sm text-neutral-600 mb-3">{desc}</p>
      <div className="flex gap-2">
        {["xlsx", "csv"].map((f) => (
          <Button key={f} className={`rounded-sm h-8 ${f === "xlsx" ? "bg-[#0047FF]" : ""}`} variant={f === "xlsx" ? "default" : "outline"} disabled={!cid || !!busy} onClick={() => gen(type, f)} data-testid={`generate-${type}-${f}-btn`}>
            <FileSpreadsheet size={14} className="mr-1" />{busy === type + f ? "Generazione…" : `Genera ${f.toUpperCase()}`}
          </Button>
        ))}
      </div>
    </Panel>
  );
  return (
    <Page title="Report" subtitle="I report sono salvati nell'archivio del cliente e non vengono mai inviati automaticamente." testid="report-page" actions={<CustomerSelect customers={customers} value={cid} onChange={setCid} />}>
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <Card type="delivery" title="Report consegna cliente" desc="Solo opportunità APPROVATE, idonee (chiamate degli ultimi 5 giorni) ed escluse le richieste di non contatto." />
        <Card type="internal" title="Report interno" desc="Tutte le registrazioni elaborate con risultati AI, tempi, esiti, revisioni ed errori. Riservato al Super Amministratore." />
      </div>
      {/* REPORT_CATEGORIE_MULTIFOGLIO */}
      <div className="mb-4">
        <Panel title="Esportazione Excel per motivazioni AI">
          <p className="text-sm text-neutral-600 mb-3">
            Seleziona le categorie da esportare. Verrà creato un unico
            file Excel con riepilogo e un foglio per ogni categoria selezionata.
            Include note AI, azione suggerita e trascrizione.
          </p>

          <div className="flex flex-wrap gap-2 mb-3">
            <Button
              size="sm"
              variant="outline"
              disabled={!!busy}
              onClick={() => setSelectedCategories(
                categories.map((c) => c.code)
              )}
            >
              Seleziona tutte
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!!busy}
              onClick={() => setSelectedCategories([])}
            >
              Deseleziona
            </Button>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3 mb-4">
            {categories.map((item) => (
              <label
                key={item.code}
                className="flex items-start gap-2 border rounded-sm p-3 cursor-pointer text-sm"
              >
                <Checkbox
                  checked={selectedCategories.includes(item.code)}
                  onCheckedChange={(checked) => {
                    setSelectedCategories((previous) =>
                      checked
                        ? [...new Set([...previous, item.code])]
                        : previous.filter((code) => code !== item.code)
                    );
                  }}
                />
                <span>{item.label}</span>
              </label>
            ))}
          </div>

          <p className="text-xs text-neutral-500 mb-3">
            {selectedCategories.length} categorie selezionate.
            Le richieste di non contatto rimangono separate
            e non costituiscono lead da richiamare.
          </p>

          {/* REPORT_PREDICTIVE_SELECTOR_V1 */}
          <div className="border rounded-sm p-3 mb-4">
            <label
              htmlFor="report-predictive"
              className="block text-sm font-semibold mb-2"
            >
              Predictive da esportare
            </label>

            <select
              id="report-predictive"
              value={predictiveId}
              onChange={(e) => setPredictiveId(e.target.value)}
              disabled={!cid || !!busy}
              className="w-full max-w-md h-9 border rounded-sm px-2 bg-white text-sm"
            >
              <option value="">
                Tutti i predictive del cliente
              </option>

              {predictives.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>

            <p className="text-xs text-neutral-500 mt-2">
              Selezionando un predictive specifico, il report
              includera soltanto le sue registrazioni.
              La scelta vale anche per il periodo selezionato.
            </p>
          </div>

          {/* REPORT_FILTRO_PERIODO_UI_V1 */}
          <div className="border rounded-sm p-3 mb-4 bg-gray-50">
            <label
              htmlFor="report-periodo"
              className="block text-sm font-semibold mb-2"
            >
              Periodo delle chiamate da esportare
            </label>

            <select
              id="report-periodo"
              value={dateMode}
              onChange={(e) => setDateMode(e.target.value)}
              className="w-full max-w-md h-9 border rounded-sm px-2 bg-white text-sm"
            >
              <option value="latest">
                Ultimo giorno disponibile
              </option>
              <option value="day">
                Seleziona un giorno specifico
              </option>
              <option value="all">
                Esporta tutto lo storico
              </option>
            </select>

            {dateMode === "day" && (
              <div className="mt-3">
                <label
                  htmlFor="report-data"
                  className="block text-sm font-medium mb-1"
                >
                  Data delle telefonate
                </label>
                <input
                  id="report-data"
                  type="date"
                  value={reportDate}
                  onChange={(e) => setReportDate(e.target.value)}
                  className="h-9 border rounded-sm px-2 bg-white"
                />
              </div>
            )}

            <p className="text-xs text-neutral-500 mt-2">
              Il filtro utilizza la data della telefonata,
              non la data di completamento dell'analisi AI.
            </p>
          </div>

          <Button
            className="bg-[#0047FF] rounded-sm"
            disabled={
              !cid ||
              !!busy ||
              !selectedCategories.length ||
              (dateMode === "day" && !reportDate)
            }
            onClick={genCategories}
            data-testid="generate-categories-xlsx-btn"
          >
            <FileSpreadsheet size={15} className="mr-2" />
            {busy === "categoriesxlsx"
              ? "Generazione in corso…"
              : "Genera Excel per categorie"}
          </Button>
        </Panel>
      </div>

      <div className="bg-white border rounded-sm overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>File</TableHead><TableHead>Tipo</TableHead><TableHead>Formato</TableHead><TableHead className="text-right">Righe</TableHead><TableHead>Generato</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {reports.map((r) => (
              <TableRow key={r.id} className="row-hover text-sm" data-testid={`report-row-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.filename}</TableCell>
                <TableCell><Tone tone={r.type === "delivery" ? "blue" : "gray"}>{r.type === "delivery" ? "Consegna" : "Interno"}</Tone></TableCell>
                <TableCell className="uppercase text-xs">{r.format}</TableCell><TableCell className="text-right tabular-nums">{r.rows}</TableCell>
                <TableCell className="text-xs">{fmtDate(r.created_at)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" className="h-7" onClick={() => download(`/reports/${r.id}/download`, r.filename).catch(notifyErr)} data-testid={`download-report-${r.id}`}><Download size={14} /></Button>
                  <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => del(r.id)} data-testid={`delete-report-${r.id}`}><Trash2 size={14} /></Button>
                </TableCell>
              </TableRow>
            ))}
            {!reports.length && <TableRow><TableCell colSpan={6} className="text-center text-neutral-500 py-8">Nessun report generato.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </Page>
  );
}
